import { useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { FormikProps } from "formik";
import * as L from "leaflet";
import { useConfirm } from "material-ui-confirm";
import { useTranslation } from "react-i18next";

import { EmissionType, HistoryState } from "src/types";
import { useEmission, useRoadTypes, useVehicleTypes } from "src/hooks";
import { useSegments } from "src/hooks/segments/useSegments";
import { useSegmentUpdateMutation } from "src/hooks/segments/useSegmentUpdateMutation";
import { useSegmentDeleteMutation } from "src/hooks/segments/useSegmentDeleteMutation";
import { useCase } from "src/hooks/useCase";
import { useScenario } from "src/hooks/useScenario";
// import { ConfirmUnsavedDialog } from "src/components/dialogs/ConfirmUnsavedDialog";
import { SegmentForm, SegmentFormData } from "src/forms/SegmentForm";
import { isReadOnly } from "src/utils/permission.utils";

interface Props {
  map: L.Map;
  onClose: (action?: HistoryState["action"]) => void;
}

export function TrafficEmissionSegmentEditPage(props: Props) {
  const { t } = useTranslation();
  const params = useParams<{
    caseId: string;
    scenarioId: string;
    emissionId: string;
    segmentId: string;
  }>();
  const confirm = useConfirm();
  const { data: acase } = useCase(params.caseId);
  const { data: scenario } = useScenario(params.scenarioId);
  const { data: roadTypes } = useRoadTypes();
  const { data: vehicleTypes } = useVehicleTypes(params.scenarioId);
  const { data: segments } = useSegments(params.scenarioId);
  const { data: emission } = useEmission(params.scenarioId, params.emissionId, EmissionType.TRAFFIC);

  const formRef = useRef<FormikProps<SegmentFormData> | null>(null);

  const updateMutation = useSegmentUpdateMutation(params.scenarioId, params.segmentId);
  const deleteMutation = useSegmentDeleteMutation(params.scenarioId, params.segmentId);

  const segment = useMemo(() => {
    if (!segments) return null;

    return segments.find((segment) => segment.segmentId.toString() === params.segmentId);
  }, [params.segmentId, segments]);

  /**
   * Effects
   */
  // ...

  /**
   * Functions
   */

  function handleDiscardChanges() {}

  function handleSave(values: SegmentFormData) {
    if (!segment) return Promise.reject();
    return updateMutation
      .mutateAsync({
        ...segment,
        ...values,
      })
      .then(() => props.onClose());
  }

  async function handleDelete() {
    if (!segment) return;

    confirm({
      confirmationButtonProps: { autoFocus: true },
      title: t("label.areYouSure"),
      description: t("confirm.deleteSegment"),
    })
      .then(deleteMutation.mutateAsync)
      .then(() => props.onClose("rerenderWMS"));
  }

  return render();

  function render() {
    if (!acase || !scenario || !emission || !segment) return null;

    return (
      <>
        <SegmentForm
          key={params.segmentId}
          data={segment}
          timefactorType={acase.timefactorType}
          vehicleTypes={vehicleTypes || []}
          roadTypes={roadTypes || []}
          readOnly={isReadOnly(scenario.permissions.updateEmission)}
          isLoading={updateMutation.isLoading}
          onSubmit={handleSave}
          onClose={() => {
            handleDiscardChanges();
            props.onClose();
          }}
          onDelete={handleDelete}
          errors={updateMutation.error?.response?.data}
          formRef={formRef}
        />
      </>
    );
  }
}
